/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { FaChevronLeft, FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import * as CampaignApi from "../../../../api/Campaign";
import { ContactApi } from "../../../../api/Contacts";
import {
  createNewSearch,
  updateSavedSearch,
} from "../../../../api/SavedSearches";
import { updateUser } from "../../../../api/User";
import { RecipientAtom } from "../../../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../../../atoms/SavedSearches";
import { UserAtom } from "../../../../atoms/UserAtom";
import { UserEmailArchiveAtom } from "../../../../atoms/UserEmailArchiveAtom";
import { IUserIRoute } from "../../../../generated/Api";
import { Flex, SpaceBetween } from "../../../../styles/general";
import { ICampaignStatus, INodeTypes } from "../../../../types/CampaignTypes";
import { IOption } from "../../../../types/CommonTypes";
import { SavedSearch } from "../../../../types/SavedSearchTypes";
import { ITagQuery } from "../../../../types/TagQueryTypes";
import { FormatEmail } from "../../../../utils/FormatEmail";
import Warning from "../../../Modal/container/Warning";
import { useModal } from "../../../Modal/Modal";
import StyledAutocompleteSelect from "../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../../StyledButton";
import StyledCheckBox from "../../../StyledCheckBox";
import StyledInput from "../../../StyledInput";
import {
  Bottom,
  Container,
  Header,
  HeaderTitle,
  SavedSearchModal,
  Wrapper,
} from "../SavedSearches";
import { IReadCampaign } from "../../../../api/Campaign";

export const IconButton = styled.div`
  height: 32px;
  width: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;

  border: 1px solid #dddddd;

  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 20px;
`;

const Subtitle = styled.div`
  color: #2f2f2f;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.18px;
  margin-bottom: 10px;
`;

const PublicText = styled.div`
  color: #707070;
  font-size: 15px;
  margin-left: 5px;
  font-size: Source Sans Pro;
`;

const ItemToolbarContainer = styled.div`
  display: flex;
  grid-gap: 8px;

  position: absolute;
  right: 0px;

  align-items: center;
  margin-top: -6px;
`;

const IconButtonWithHover = styled(IconButton)`
  color: #aaaaaa !important;
  border: none;

  &:hover {
    color: #fb972e !important;
  }
`;

type Props =
  | {
      search: SavedSearch;
      isEdit: true;
      tagQuery?: ITagQuery;
      handlePage: (page: SavedSearchModal) => void;
      handleSavedSearch: React.Dispatch<React.SetStateAction<SavedSearch>>;
      handleCreateFooter: (vle: boolean) => void;
    }
  | {
      search?: SavedSearch;
      isEdit?: boolean;
      tagQuery?: ITagQuery;
      handlePage: (page: SavedSearchModal) => void;
      handleSavedSearch: React.Dispatch<React.SetStateAction<SavedSearch>>;
      handleCreateFooter: (vle: boolean) => void;
    };

const IsLoadingCreateEdit = atom<boolean>({
  key: "CreateEditSearchIsLoadingDeleteFooterlAtom",
  default: false,
});

export default function CreateEditSearchModal(props: Props): JSX.Element {
  const {
    search,
    tagQuery,
    isEdit,
    handlePage,
    handleSavedSearch,
    handleCreateFooter,
  } = props;
  const setCountedSavedSearches = useSetRecoilState(CountedSavedSearchesAtom);
  const [user, setUser] = useRecoilState(UserAtom);
  const [savedSearches, setSavedSearches] = useRecoilState(
    CountedSavedSearchesAtom
  );
  const setSelectedRecipient = useSetRecoilState(RecipientAtom);
  const modal = useModal();
  const countedSavedSearches = useRecoilValue(CountedSavedSearchesAtom);

  const [footerOptions, setFooterOptions] = useState(
    search
      ? user!.emailOptions!.footers!.find((f) => f.id === search.footerId)!
      : user?.emailOptions.footers[0]
  );
  const [isLoading, setIsLoading] = useRecoilState(IsLoadingCreateEdit);

  const [isPublicArchive, setIsPublicArchive] = useState(
    search?.publicArchive || false
  );
  const [campaigns, setCampaigns] = useState<IReadCampaign[]>();

  const { name } = footerOptions || user?.emailOptions.footers[0];

  const options = user!.emailOptions!.footers!.map((f) => ({
    value: f.id as string,
    name: f.name as string,
  }));

  const [selectedFooter, setSelectedFooter] = useState(
    options.find((o) => o.name === name)
  );
  const { footer } = FormatEmail.format("", user as IUserIRoute, {
    footer: footerOptions || user?.emailOptions.footers[0],
  });

  console.log({ search });

  const [nameSaved, setNameSaved] = useState(search?.name || "");

  const userEmailArchive = useRecoilValue(UserEmailArchiveAtom);

  useEffect(() => {
    (async () => {
      const campaings = await CampaignApi.readAllCampaigns();
      setCampaigns(campaings);
    })();
  }, []);

  useEffect(() => {
    if (search && isEdit) {
      setNameSaved(search.name);
      setFooterOptions(
        user!.emailOptions!.footers!.find((f) => f.id === search.footerId)!
      );
    }
  }, [search]);

  useEffect(() => {
    setFooterOptions(
      user!.emailOptions!.footers!.find((f) => f.id === selectedFooter?.value)!
    );
  }, [selectedFooter]);

  function updateSearch(footerId: string) {
    setCountedSavedSearches((s) =>
      s.map((searchFromMap) => {
        if (searchFromMap.id === search.id)
          return {
            ...searchFromMap,
            footerId,
            name: nameSaved,
            publicArchive: isPublicArchive,
          };
        return searchFromMap;
      })
    );
  }

  async function createOrSave() {
    if (!user || isLoading) return;

    if (!isEdit) {
      if (nameSaved.length === 0) {
        toast("A saved search requires a name", { type: "error" });
        setIsLoading(false);
        return;
      }
      const newSearch = {
        name: nameSaved,
        search: tagQuery,
        footerId: selectedFooter.value,
        publicArchive: isPublicArchive,
        labels: [],
      };

      const createdSearchId = await createNewSearch(newSearch);

      const createdSearch: SavedSearch = {
        ...newSearch,
        id: createdSearchId,
        userUid: user!.userUid as string,
      };

      const response = await ContactApi.countContactsByTagQueries([
        createdSearch.search,
      ]);

      const contactsCount = response[0];

      const countedNewSearch = {
        ...createdSearch,
        contactsCount,
      };
      setSavedSearches([...savedSearches, countedNewSearch]);

      setSelectedRecipient((prevSelected) => ({
        ...prevSelected,
        count: countedNewSearch.contactsCount,
        tagQuery: countedNewSearch.search,
        setted: "savedSearch",
        footerId: countedNewSearch.footerId,
        searchName: countedNewSearch.name,
        searchId: countedNewSearch.id,
        showOnArchive: countedNewSearch.publicArchive,
      }));

      toast("Saved search created", { type: "success" });
      modal.popAll();
    } else {
      const isSuccessUpdateSearch = await updateSavedSearch(search.id, {
        name: nameSaved,
        publicArchive: isPublicArchive,
        footerId: footerOptions.id,
      });

      if (isSuccessUpdateSearch) {
        updateSearch(footerOptions.id);

        toast("Saved search updated", { type: "success" });
        modal.popModal();
      }
    }

    setIsLoading(false);
  }

  async function deleteFooter(option: IOption) {
    if (isLoading) return;

    setIsLoading(true);

    if (
      // user.emailOptions.footers[0].id === option.value ||
      // selectedFooter.value === option.value
      user.emailOptions.footers.length === 1
    ) {
      toast("Footer selected is disabled to remove", { type: "warning" });
      setIsLoading(false);
      modal.popModal();
      return;
    }

    if (countedSavedSearches.find((s) => s.footerId === option.value)) {
      toast("There is a saved search using this footer", { type: "warning" });
      setIsLoading(false);
      modal.popModal();
      return;
    }

    const campaignNodeWithFooter = campaigns.find(
      (campaign) =>
        (campaign.status === ICampaignStatus.Active ||
          campaign.status === ICampaignStatus.Inactive ||
          campaign.status === ICampaignStatus.Draft) &&
        !!campaign.nodes.find(
          (node) =>
            node.type === INodeTypes.Email &&
            node.email.footerId === option.value
        )
    );

    if (campaignNodeWithFooter) {
      toast("There is a campaign using this footer", { type: "warning" });
      setIsLoading(false);
      modal.popModal();
      return;
    }

    const newFooterArray = user.emailOptions?.footers!.filter(
      (footer) => footer.id !== option.value
    );

    const isSuccessUpdateUser = await updateUser({
      emailOptions: {
        ...user.emailOptions,
        footers: newFooterArray,
      },
    });

    if (isSuccessUpdateUser) {
      setUser({
        ...user,
        emailOptions: {
          ...user.emailOptions,
          footers: newFooterArray,
        },
      });

      setSelectedFooter({
        value: newFooterArray[0].id as string,
        name: newFooterArray[0].name as string,
      });

      setIsLoading(false);
      toast("Footer deleted", { type: "success" });
      modal.popModal();
    }
  }

  return (
    <Container>
      <Header>
        <Flex>
          {isEdit && (
            <div
              css={css`
                cursor: pointer;
                height: 15px;
              `}
              onClick={() => handlePage(SavedSearchModal.MANAGE)}
            >
              <FaChevronLeft color="#BBBBBB" size={15} />
            </div>
          )}

          <HeaderTitle
            css={
              isEdit &&
              css`
                margin-left: 5px;
              `
            }
          >
            {isEdit ? "Edit Saved Search" : "New Saved Search"}
          </HeaderTitle>
        </Flex>

        <div
          css={css`
            cursor: pointer;
          `}
        >
          <IoMdClose
            color="#868482"
            size={25}
            onClick={() => modal.popModal()}
          />
        </div>
      </Header>

      <Wrapper>
        <InputContainer
          css={css`
            margin-top: 20px;
          `}
        >
          <Subtitle>Name</Subtitle>
          <StyledInput
            value={nameSaved}
            onChange={(evt) => {
              setNameSaved(evt.target.value);
              if (isEdit) {
                handleSavedSearch({ ...search, name: evt.target.value });
              }
            }}
          />
        </InputContainer>

        <InputContainer>
          <Subtitle>Footer</Subtitle>
          <Flex>
            <StyledAutocompleteSelect
              selectedOption={selectedFooter}
              onOptionSelect={(option) => {
                setSelectedFooter(option);

                if (option.value === "") return;

                if (!isEdit) return;
                const footer = user!.emailOptions!.footers!.find(
                  (f) => f.id === option?.value
                )!;
                handleSavedSearch({ ...search, footerId: footer.id });
              }}
              placeholder="Select a Footer"
              options={options}
              removeCheckIcon
              renderCustomItemToolbar={
                (option) =>
                  options[0].value !== option.value && (
                    // options.length > 1 && (
                    <ItemToolbarContainer>
                      <IconButtonWithHover
                        onClick={(e) => {
                          e.stopPropagation();
                          modal.pushModal(
                            <Warning
                              IsLoadingAtom={IsLoadingCreateEdit}
                              icon={"./icons/delete.svg"}
                              title="Delete Footer"
                              description={
                                "Are you sure you want to delete this footer?"
                              }
                              buttonsName={["No", "Yes"]}
                              onOkClicked={() => {
                                deleteFooter(option);
                                modal.popModal();
                              }}
                              onCancelClicked={() => modal.popModal()}
                            />
                          );
                        }}
                      >
                        <FaRegTrashAlt />
                      </IconButtonWithHover>
                    </ItemToolbarContainer>
                  )
                // )
              }
            />

            <StyledButton
              inverted
              css={css`
                margin-left: 15px;
                height: 42px;
                width: 150px;
                padding: 5px;
              `}
              onClick={() => {
                handlePage(SavedSearchModal.FOOTER);
                handleCreateFooter(true);

                // Save the name in the upper state to save the
                // current written name
                handleSavedSearch((s) => ({ ...s, name: nameSaved }));
              }}
            >
              Create New
            </StyledButton>
          </Flex>
        </InputContainer>

        <InputContainer>
          <SpaceBetween>
            <Subtitle>Footer Preview</Subtitle>

            <Flex
              css={css`
                cursor: pointer;
              `}
              onClick={() => {
                handlePage(SavedSearchModal.FOOTER);
                handleCreateFooter(false);

                // Save the name in the upper state to save the
                // current written name
                handleSavedSearch((s) => ({ ...s, name: nameSaved }));
              }}
            >
              <FaPencilAlt color={"#FB972E"} size={13} />
              <Subtitle
                css={css`
                  margin-bottom: 0px;
                  margin-left: 5px;
                  color: #fb972e;
                `}
              >
                Edit
              </Subtitle>
            </Flex>
          </SpaceBetween>

          <div
            css={css`
              white-space: pre-wrap;
              background-color: #f4f4f4;
              padding: 10px;
              border-radius: 4px;
              width: 100%;
            `}
            dangerouslySetInnerHTML={{ __html: footer }}
          />
        </InputContainer>

        {userEmailArchive && userEmailArchive.enabled && (
          <InputContainer
            css={css`
              padding-bottom: 40px;
            `}
          >
            <Subtitle>Public Archive</Subtitle>
            <Flex>
              <StyledCheckBox
                check={isPublicArchive}
                handleCheck={() => setIsPublicArchive(!isPublicArchive)}
              />
              <PublicText
                onClick={() => setIsPublicArchive(!isPublicArchive)}
                css={css`
                  cursor: default;
                `}
              >
                Allow emails sent to this saved search in the public archive
              </PublicText>
            </Flex>
          </InputContainer>
        )}
      </Wrapper>
      <Bottom>
        <StyledButton
          inverted
          onClick={() => modal.popModal()}
          style={{ marginRight: "10px", width: "92px" }}
        >
          Cancel
        </StyledButton>
        <StyledButton
          style={{ width: "92px" }}
          isLoading={isLoading}
          onClick={() => {
            if (!selectedFooter || selectedFooter.value === "") {
              return toast("A saved search requires a footer", {
                type: "info",
              });
            }

            setIsLoading(true);

            if (isLoading) return;

            createOrSave();
          }}
        >
          Save
        </StyledButton>
      </Bottom>
    </Container>
  );
}
